/**
 * Formatters shared across the FinancialsV2 KPI components.
 *
 * Kept separate from `NumberFormatter.js` because the financial dashboards
 * render whole-dollar headline figures (no cents), whereas table formatters
 * in `NumberFormatter.js` need cent-level precision.
 */

// `decimals` defaults to 0 for the FinancialsV2 headline figures, but callers
// that need cents (Affiliates tables, Payments columns) can pass `2`.
export const formatCurrency = (
  value: number,
  symbol: string = "$",
  decimals: number = 0,
): string => {
  // Sign goes outside the symbol: "-$500", not "$-500".
  const sign = value < 0 ? "-" : "";
  const abs = Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(value));
  return `${sign}${symbol}${abs}`;
};

// Plain integer formatting for count-style metrics (users, FTDs, etc.).
// Pinned to en-US so the output stays deterministic across runtime locales,
// matching how `formatCurrency` is locale-pinned for the same reason.
export const formatCount = (value: number): string =>
  Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);

export const formatCompactCurrencyTick = (
  value: number,
  symbol: string = "$",
): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(2)}M`;
  const kRounded = Math.round(abs / 1_000);
  // If rounding to k would produce >= 1000k, display as M instead.
  if (kRounded >= 1_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${kRounded}k`;
  return `${sign}${symbol}${Math.round(abs)}`;
};

export const formatCompactCountTick = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  const kRounded = Math.round(abs / 1_000);
  // If rounding to k would produce >= 1000k, display as M instead.
  if (kRounded >= 1_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${kRounded}k`;
  return `${sign}${Math.round(abs)}`;
};
